/**
 * Score → label/color map, ported from the Ember `password-scores` util so the
 * wording matches what users saw before the migration. Scoring lives in `./scorer`.
 */

/** Subset of Pluma `Label` colors, checked against `<Label>` at the usage site. */
export type PasswordLabelColor = 'red' | 'yellow' | 'green' | 'grey';

export type PasswordScore = {
  label: string;
  labelColor: PasswordLabelColor;
};

const INVALID_SCORE: PasswordScore = { label: 'Invalid', labelColor: 'red' };
const WEAK_SCORE: PasswordScore = { label: 'Weak', labelColor: 'red' };
const FAIR_SCORE: PasswordScore = { label: 'Fair', labelColor: 'yellow' };
const GOOD_SCORE: PasswordScore = { label: 'Good', labelColor: 'green' };
const STRONG_SCORE: PasswordScore = { label: 'Strong', labelColor: 'green' };

// No reading yet, or scoring failed. Deliberately neutral grey, not green — a
// missing score must never read as a pass (fail closed). Ember showed a green
// "Valid" here; the gate now lives with the caller (see usePasswordStrength's
// `error` and the score >= 3 check the set-password form owns).
const UNKNOWN_SCORE: PasswordScore = { label: 'Unknown', labelColor: 'grey' };

/** Indexed by zxcvbn score 0–4, weakest to strongest. */
export const PASSWORD_SCORES: readonly PasswordScore[] = [
  INVALID_SCORE,
  WEAK_SCORE,
  FAIR_SCORE,
  GOOD_SCORE,
  STRONG_SCORE,
];

export function getPasswordScore(score: number | undefined): PasswordScore {
  if (score === undefined) {
    return UNKNOWN_SCORE;
  }

  return PASSWORD_SCORES[score] ?? UNKNOWN_SCORE;
}
